import {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import cleanFieldValue from 'utils/cleanFieldValue';

const QuickViewCard = ({data, url}) => {
  const listRef = useRef();
  useEffect(() => {
    // eslint-disable-next-line no-undef
    Drupal.attachBehaviors(listRef.current);
  }, []);

  const intl = useIntl();
  const emptyLabel = intl.formatMessage({id: 'ProgramCard.Label.None'});
  const {
    title,
    program_image_quick_view,
    regions,
    displayed_destination,
    field_prgm_locality,
    field_short_title,
    field_prgm_type,
    field_prgm_rank_group,
    field_prgm_term_type,
    field_prgm_delivery_type,
    field_short_desc,
    program_offers,
    // -- At a glance fields
    field_prgm_lang_instruct,
    field_prgm_lang_pre_lvl,
    prgm_gpa_req,
    field_prgm_academic_level,
    field_prgm_credits,
    field_prgm_strengths,
    field_prgm_housing_opt,
    field_prgm_part_intern,
    field_prgm_visa,
    field_prgm_status
  } = data || {};

  const [activeTab, setActiveTab] = useState(0);

  const glaceItems = [
    {
      label: intl.formatMessage({id: 'ProgramQuickView.LanguageInstruction.Label'}),
      value: cleanFieldValue(field_prgm_lang_instruct, emptyLabel),
    },
    {
      label: intl.formatMessage({id: 'ProgramQuickView.languagePrerequisiteLevel.Label'}),
      value: cleanFieldValue(field_prgm_lang_pre_lvl, emptyLabel),
    },
    {
      label: intl.formatMessage({id: 'ProgramQuickView.GPA.Label'}),
      value: cleanFieldValue(prgm_gpa_req >= 0 ? prgm_gpa_req.toFixed(2) : emptyLabel, emptyLabel),
    },
    {
      label: intl.formatMessage({id: 'ProgramQuickView.AcademicLevel.Label'}),
      value: cleanFieldValue(field_prgm_academic_level, emptyLabel),
    },
    {
      label: intl.formatMessage({id: 'ProgramQuickView.Credits.Label'}),
      value: cleanFieldValue(field_prgm_credits, emptyLabel),
    },
    {
      label: intl.formatMessage({id: 'ProgramQuickView.AcademicStrengths.Label'}),
      value: cleanFieldValue(field_prgm_strengths, emptyLabel),
    },
    {
      label: intl.formatMessage({id: 'ProgramQuickView.Housing.Label'}),
      value: cleanFieldValue(field_prgm_housing_opt, emptyLabel),
    },
    {
      label: intl.formatMessage({id: 'ProgramQuickView.Internship.Label'}),
      value: cleanFieldValue(field_prgm_part_intern, emptyLabel),
    },
    {
      label: intl.formatMessage({id: 'ProgramQuickView.Visa.Label'}),
      value: cleanFieldValue(field_prgm_visa, emptyLabel),
    },
  ];

  if (!data) {
    return null;
  }

  const programOffers = () => {
    if (!program_offers) {
      return '';
    }
    else if (typeof program_offers === 'object') {
      const offers = program_offers.map((item) => {
        return JSON.parse(item);
      });

      return (
        <div
          className="offers-about offers-about--quick-view flex flex-col mb-34">
          <div
            className="flex justify-start border-b border-grey-veryLight mb-24">
            {offers.map((offer, index) => {
              return (
                <h5
                  className={`js-offers-about-tab offers-about--quick-view__tab py-16 ${activeTab === index ? 'active' : ''}`}
                  onClick={() => changeTabIndex(index)}>
                  {offer.label}
                </h5>
              );
            })}
          </div>
          <div className="js-offers-content offers-about__content">
            {offers.map((offer, index) => {
              return (
                <div className={activeTab === index ? 'active' : ''}
                     dangerouslySetInnerHTML={{__html: offer.offer_render}}/>
              );
            })}
          </div>
        </div>
      );
    }
    else if (typeof program_offers === 'string') {
      const offers = JSON.parse(program_offers);

      return (
        <div
          className="offers-about offers-about--quick-view flex flex-col mb-34">
          <div
            className="flex justify-start border-b border-grey-veryLight mb-24">
            <h5
              className="js-offers-about-tab offers-about--quick-view__tab py-16 active">
              {offers.label}
            </h5>
          </div>
          <div className="js-offers-content offers-about__content">
            <div className="active"
                 dangerouslySetInnerHTML={{__html: offers.offer_render}}/>
          </div>
        </div>
      );
    }
  };

  const changeTabIndex = (index) => {
    setActiveTab(index);
  };

  const programType = () => {
    const classes = field_prgm_type.toLowerCase() === 'standard' ? 'link--tag link--tag-pink inline-flex'
      : 'link--tag inline-flex';
    const text = field_prgm_type.toLowerCase() === 'standard' ? 'Study Abroad'
      : field_prgm_type.toLowerCase() === 'internship' ? 'Full-time Internship' : field_prgm_type;

    return (
      <span className={classes}>{text}</span>
    )
  };

  const media = () => {
    return (
      <div className="program-quick-view__image-container">
        <div dangerouslySetInnerHTML={{__html: program_image_quick_view}}></div>
      </div>
    );
  };

  const heading = () => {
    return (
      <h2 className="program--title mb-16">
        <a href={url}
           className="text-blue-default no-underline link--custom-underline">
          {field_short_title ? field_short_title : title}
        </a>
      </h2>
    );
  };

  const renderDestination = () => {
    if (!displayed_destination) {
      return '';
    }

    const format = (destination, regionIndex) => {
      destination = destination.replace(', ', ' · ');
      const pickedRegion = typeof regionIndex !== 'undefined' ? regions[regionIndex] : regions;

      return destination.includes(pickedRegion) ? destination : `${pickedRegion} · ${destination}`;
    }

    if (typeof displayed_destination === 'object') {
      return displayed_destination.map((destination, index) => {
        return format(destination, index);
      }).join(' | ');
    }

    return format(displayed_destination);
  };

  const quickView = () => {

    return (
      <article
        className="program program-quick-view flex shadow-default bg-white">
        {media()}

        <div
          className="program-quick-view__details-container pt-40 px-36 pb-20 flex relative w-full">

          <div className="flex-col">
            {/*Program Location*/}
            <div className="program--location-wrapper flex justify-between">
              {field_prgm_locality &&
                <div
                  className="program--location text-grey-medium mb-8 text-14">
                  {renderDestination()}
                </div>
              }
            </div>

            {heading()}

            {
              /*Program Type*/
              field_prgm_type &&
              <div className="program--type mb-22 flex items-start">
                {field_prgm_status === 0 ?
                  <span class="link--tag link--tag-suspended inline-flex mr-10">
                    {intl.formatMessage({id: 'ProgramCard.Status.Label'})}
                  </span> : ''
                }
                {programType()}
                {field_prgm_rank_group &&
                  <span
                    className="program--rank ml-10 link--tag link--tag-pink inline-flex">
                    {field_prgm_rank_group}
                  </span>
                }
              </div>
            }

            <div className="flex mb-22">
              <div className="program--length uppercase font-bold text-12">
                <label className="text-grey-text inline mr-5">
                  {intl.formatMessage({id: 'ProgramQuickView.Length.Label'})}:&nbsp;
                </label>
                {cleanFieldValue(field_prgm_term_type, emptyLabel)}
              </div>

              <div className="program--options uppercase font-bold text-12">
                <label className="inline text-grey-text mr-5">
                  <span className="mx-10">•</span>
                  {intl.formatMessage({id: 'ProgramQuickView.Options.Label'})}:&nbsp;
                </label>
                {cleanFieldValue(field_prgm_delivery_type, emptyLabel)}
              </div>
            </div>

            <div className="program-description mb-32">
              <p>{field_short_desc}</p>

              {field_short_desc ?
                <a href={url} className="link--black font-bold underline">
                  {intl.formatMessage({id: 'ProgramQuickView.ReadMore.CTA'})}
                </a>
                : ''}
            </div>

            {programOffers()}

            <a
              className="button button--small mr-5 mb-16 js-dL-programCardSelected"
              href={url}>
              {intl.formatMessage({id: 'ProgramQuickView.ProgramDetails.CTA'})}
            </a>

            <a className="button button--small button--secondary"
               href="/admissions">
              {intl.formatMessage({id: 'ProgramQuickView.ApplyNow.CTA'})}
            </a>
          </div>

          <div
            className="program-quick-view__glance flex-col hidden lg:block p-30 pb-0 bg-purple-lighter ml-50">
            <h4 className="mb-16">
              {intl.formatMessage({id: 'ProgramQuickView.AtGlance.Label'})}
            </h4>

            <div className="flex flex-col">
              {glaceItems.map((item) => {
                return (
                  <div className="glance-item flex md:mb-20">
                    <div>
                      <div
                        className="glance-item__heading text-16 font-bold text-grey-default">
                        {item.label}:&nbsp;
                      </div>
                      <div className="glance-item__description">
                        {item.value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <>
      {quickView()}
    </>
  )
};

export default QuickViewCard;
